import { useTranslation } from 'react-i18next';
import { Play, Save, Settings2, Loader2, Info, Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectLabel, SelectGroup, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { NO_PROFILE, profileLabel, profileDirty } from './profileUtils';

export { NO_PROFILE };

const NumberField = ({ id, label, hint, value, onChange, ...rest }) => (
    <div className="space-y-1.5">
        <Label htmlFor={id}>{label}</Label>
        <Input id={id} type="number" value={value} onChange={(e) => onChange(e.target.value)}
            data-testid={id} {...rest} />
        {hint ? <p className="text-[11px] text-muted-foreground">{hint}</p> : null}
    </div>
);

/**
 * Prospect / Freestyle inputs: typed-in numbers plus the editable assumptions,
 * with a saved-profile loader. Nothing here reads or writes a client record.
 */
export const FreestyleInputs = ({
    value, onChange, profiles, activeProfile, onLoadProfile, onSaveAs, onManage, onRun, loading,
}) => {
    const { t } = useTranslation();
    const set = (patch) => onChange({ ...value, ...patch });

    const builtins = profiles.filter((p) => p.is_builtin);
    const mine = profiles.filter((p) => !p.is_builtin);
    const loaded = profiles.find((p) => p.id === activeProfile) || null;
    const dirty = loaded?.is_builtin && profileDirty(loaded, value);
    const disclaimer = loaded?.disclaimer;

    return (
        <Card data-testid="calc-freestyle-card">
            <CardHeader className="pb-3">
                <CardTitle className="text-base">{t('salesCalc.freestyle.title', 'Prospect numbers')}</CardTitle>
                <CardDescription>{t('salesCalc.mode.freestyleHint', 'Type the numbers in — nothing is read from or written to a client record.')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
                <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
                    <div className="space-y-1.5 md:col-span-1">
                        <Label htmlFor="calc-fs-name">{t('salesCalc.freestyle.prospectName', 'Prospect name (optional)')}</Label>
                        <Input id="calc-fs-name" value={value.prospect_name}
                            placeholder={t('salesCalc.freestyle.prospectNamePlaceholder', 'e.g. Acme Motors')}
                            maxLength={120}
                            onChange={(e) => set({ prospect_name: e.target.value })}
                            data-testid="calc-fs-name" />
                    </div>
                    <NumberField id="calc-fs-rating" min="0" max="5" step="0.1"
                        label={t('salesCalc.freestyle.currentRating', 'Current rating')}
                        value={value.current_rating} onChange={(v) => set({ current_rating: v })} />
                    <NumberField id="calc-fs-reviews" min="0" step="1"
                        label={t('salesCalc.freestyle.currentReviews', 'Current review count')}
                        value={value.current_reviews} onChange={(v) => set({ current_reviews: v })} />
                    <NumberField id="calc-fs-permonth" min="0" step="1"
                        label={t('salesCalc.freestyle.perMonth', 'New reviews per month')}
                        value={value.projected_reviews_per_month}
                        onChange={(v) => set({ projected_reviews_per_month: v })} />
                    <NumberField id="calc-fs-months" min="1" max="60" step="1"
                        label={t('salesCalc.freestyle.months', 'Months')}
                        value={value.months} onChange={(v) => set({ months: v })} />
                    {/* Prospect-specific, NOT an industry assumption — so it sits
                        here with the other prospect numbers, never in a profile. */}
                    <NumberField id="calc-fs-revenue" min="0" step="1000" placeholder="—"
                        label={t('salesCalc.freestyle.annualRevenue', 'Annual revenue (optional)')}
                        hint={t('salesCalc.freestyle.annualRevenueHint', 'Leave blank to show sales impact as a percentage only.')}
                        value={value.annual_revenue} onChange={(v) => set({ annual_revenue: v })} />
                </div>

                <div className="rounded-lg border p-4 space-y-4" data-testid="calc-fs-metrics">
                    <div className="flex flex-wrap items-end justify-between gap-3">
                        <div>
                            <p className="font-medium text-sm text-slate-900 dark:text-white">
                                {t('salesCalc.freestyle.metricsTitle', 'Assumptions')}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                {t('salesCalc.profiles.builtinHint', 'Built-in industry starters everyone gets — load one, then adjust or save your own copy.')}
                            </p>
                        </div>
                        <div className="flex flex-wrap items-end gap-2">
                            <div className="space-y-1.5">
                                <Label className="text-xs text-muted-foreground">{t('salesCalc.profiles.loadFrom', 'Load metrics from')}</Label>
                                <Select value={activeProfile || NO_PROFILE} onValueChange={onLoadProfile}>
                                    <SelectTrigger className="w-[220px]" data-testid="calc-profile-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={NO_PROFILE}>{t('salesCalc.profiles.none', 'No profile')}</SelectItem>
                                        {builtins.length ? (
                                            <SelectGroup>
                                                <SelectLabel>{t('salesCalc.profiles.groupBuiltin', 'Industry starters')}</SelectLabel>
                                                {builtins.map((p) => (
                                                    <SelectItem key={p.id} value={p.id} data-testid={`calc-profile-option-${p.builtin_key}`}>
                                                        {profileLabel(p, t)}
                                                    </SelectItem>
                                                ))}
                                            </SelectGroup>
                                        ) : null}
                                        {mine.length ? (
                                            <SelectGroup>
                                                <SelectLabel>{t('salesCalc.profiles.groupMine', 'Your profiles')}</SelectLabel>
                                                {mine.map((p) => (
                                                    <SelectItem key={p.id} value={p.id}>{p.profile_name}</SelectItem>
                                                ))}
                                            </SelectGroup>
                                        ) : null}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button type="button" variant="outline" size="sm" onClick={onSaveAs} data-testid="calc-profile-saveas-btn">
                                <Save className="w-4 h-4 mr-1.5" />{t('salesCalc.profiles.saveAs', 'Save as profile')}
                            </Button>
                            <Button type="button" variant="ghost" size="sm" onClick={onManage} data-testid="calc-profile-manage-btn">
                                <Settings2 className="w-4 h-4 mr-1.5" />{t('salesCalc.profiles.manage', 'Profiles')}
                            </Button>
                        </div>
                    </div>

                    {dirty ? (
                        <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-400/60 bg-amber-50 dark:bg-amber-950/20 px-3 py-2"
                            data-testid="calc-profile-dirty-notice">
                            <Lock className="w-4 h-4 text-amber-700 dark:text-amber-300 shrink-0" />
                            <p className="text-xs text-amber-800 dark:text-amber-200 flex-1 min-w-[220px]">
                                {t('salesCalc.profiles.dirtyNotice', 'You\'ve changed the values loaded from "{{name}}". Built-in profiles can\'t be overwritten — save your own copy instead.', { name: profileLabel(loaded, t) })}
                            </p>
                            <Button type="button" size="sm" variant="outline" onClick={onSaveAs}
                                data-testid="calc-profile-saveas-new-btn">
                                <Save className="w-4 h-4 mr-1.5" />{t('salesCalc.profiles.saveAsNew', 'Save as new profile')}
                            </Button>
                        </div>
                    ) : null}

                    <div className="grid gap-4 md:grid-cols-4 items-end">
                        <NumberField id="calc-fs-value" min="0" step="0.5"
                            label={t('salesCalc.freestyle.valuePerReview', 'Value per review')}
                            value={value.value_per_review} onChange={(v) => set({ value_per_review: v })} />
                        <NumberField id="calc-fs-pct" min="0" max="100" step="0.5"
                            label={t('salesCalc.freestyle.pctPerStar', '% sales lift per full star')}
                            value={value.sales_impact_pct_per_star}
                            onChange={(v) => set({ sales_impact_pct_per_star: v })} />
                        <NumberField id="calc-fs-negcost" min="0" step="10"
                            label={t('salesCalc.freestyle.negativeCost', 'Cost of one negative review')}
                            value={value.negative_review_cost}
                            onChange={(v) => set({ negative_review_cost: v })} />
                        <NumberField id="calc-fs-negrate" min="0" max="100" step="1"
                            label={t('salesCalc.freestyle.negativeRate', '% of reviews assumed negative without intervention')}
                            hint={t('salesCalc.freestyle.negativeHint', 'The platform holds no 1★–5★ breakdown, so the negative share is an explicit assumption you control.')}
                            value={value.assumed_negative_rate_pct}
                            onChange={(v) => set({ assumed_negative_rate_pct: v })} />
                    </div>

                    {/* Modeled figures never travel without their caveat — visible
                        on screen, and printed on the one-pager. */}
                    {disclaimer ? (
                        <div className="rounded-md border border-sky-400/60 bg-sky-50 dark:bg-sky-950/20 p-3"
                            data-testid="calc-profile-disclaimer">
                            <p className="flex items-center gap-1.5 text-xs font-semibold text-sky-900 dark:text-sky-200">
                                <Info className="w-3.5 h-3.5" />
                                {t('salesCalc.profiles.disclaimerTitle', 'How these automotive figures were modeled')}
                            </p>
                            <p className="mt-1 text-[11px] leading-relaxed text-sky-900/90 dark:text-sky-100/80">
                                {t(`salesCalc.profiles.disclaimer.${disclaimer}`, '')}
                            </p>
                        </div>
                    ) : null}
                </div>

                <Button type="button" onClick={onRun} disabled={loading} data-testid="calc-fs-run-btn">
                    {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Play className="w-4 h-4 mr-1.5" />}
                    {t('salesCalc.freestyle.run', 'Run the numbers')}
                </Button>
            </CardContent>
        </Card>
    );
};

export default FreestyleInputs;
